import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getRefinerFields, formatDateInput } from '../Helpers/DatatableHelpers';
import fields from '../../../Generic/Appointments/Components/Updater/Field.constants';
import viewUpdates from '../../../Generic/Appointments/Components/Updater/ViewUpdate.constants';
import { 
	clearRefineData,
	setGroupUpdaterResultsDetails,
	clearGroupUpdaterData,
	getGroupUpdaterUpdates,
	clearRefineFilters
} from '../../../../../Redux/Actions/Script.Dates.Actions';

function useScriptDates (handleClearScriptDateUpdates, handleGetScriptDates, saveGroupUpdater) {
    
	const dispatch = useDispatch();	
	const [pageSize, setPageSize] = useState(10);
	const [isPageReset, setIsPageReset] = useState(false);
	const [year, updateYear] = useState('2019');
	const [isFiltering, setIsFiltering] = useState(true);
	const [filterOpen, setFilterOpen] = useState(true);	
	const [isYearChange, setIsYearChange] = useState(false);	
	const [refinerOpen, setRefinerOpen] = useState(false);
	const [updaterOpen, setUpdaterOpen] = useState(false);
	const [viewDataOpen, setViewDataOpen] = useState(false);
    const [displayAppointmentsName, setDisplayAppointmentsName] = useState('');
	const [inPreview, setInPreview] = useState(false);	
	const [isRefineRemove, setIsRefineRemove] = useState(false);
	const [isRefineAllClear, setIsRefineAllClear] = useState(false);
	const [previewUpdate, setPreviewUpdate] = useState([]);
	const [updatedIds, setUpdatedIds] = useState([]);
	const [failedIds, setFailedIds] = useState([]);
	const [isSave, setIsSave] = useState(false);
	
	const toggleFilter = useCallback(() => setFilterOpen(prev => !prev), []);
	const toggleRefiner = useCallback(() => setRefinerOpen(prev => !prev), []);
	const toggleUpdater = useCallback(() => setUpdaterOpen(prev => !prev), []);
	const toggleViewData = useCallback(() => setViewDataOpen(prev => !prev), []);
	const refinerFields = useMemo(() => getRefinerFields('scriptDates'), ['scriptDates']);

	const types = useSelector(state => state.contract_globals.appointment_types.selected);
	const isLoaded = useSelector(state => state.scriptdates.isLoaded) || false;
	const selectedTypes = useSelector(state => state.scriptdates.selectedTypes) || types;	
	const selectedTeamIds = useSelector(state => state.scriptdates.selectedTeamIds) || [];
	const refineData = useSelector(state => state.scriptdates.refineData) || [];
	const refineFields = useSelector(state => state.scriptdates.refineFields) || [{ title: '', markingTeamCode: '', markingMethod: [], scriptDeliveryDate: '', scriptReturnDate: '', eMarkingStartDate: '', eMarkingEndDate: ''}];	
	const groupUpdaterFields = useSelector(state => state.scriptdates.groupUpdaterFields) || [{ isGroupUpdater: false, previewUpdate: [], isGroupUpdaterFilter: false }];		
	const groupUpdaterData = useSelector(state => state.scriptdates.groupUpdaterData) || [];		

	const handleClearRefineData = () => dispatch(clearRefineData());
	const handleGroupUpdaterResultsDetails = (groupUpdaterTypes, groupUpdaterTeamIds) => dispatch(setGroupUpdaterResultsDetails(groupUpdaterTypes, groupUpdaterTeamIds));
	const handleClearGroupUpdaterData = () => dispatch(clearGroupUpdaterData());
	const handleGetGroupUpdaterUpdates = () => dispatch(getGroupUpdaterUpdates());	
	const handleClearRefineFilters = () => dispatch(clearRefineFilters());

    const startNewFilter = () => {
		setIsFiltering(true);
		setFilterOpen(true);
		setViewDataOpen(false);
		setDisplayAppointmentsName('');
		setInPreview(false);
		setIsRefineRemove(false);	
		setIsRefineAllClear(false);	
		setRefinerOpen(false);
		setPageSize(10);
	};

	const handleFilterClear = useCallback(() => {
        setViewDataOpen(false);	
		setDisplayAppointmentsName('');
		setPageSize(10);
    }, []);	

	const handleFilterSubmit = useCallback(async(selectedYear, selectedFilteredTypes, selectedFilteredTeamIds) => {
		const selectedTypes = selectedFilteredTypes ? selectedFilteredTypes : types;
		const selectedTeamIds = selectedFilteredTeamIds ? selectedFilteredTeamIds : [];
		setIsFiltering(false);
		setFilterOpen(false);
		setViewDataOpen(true);
		setIsYearChange(false);

		const exportData = false;	
		const query = { sortOrder: 'asc', sortField: 'title', page: 1, sizePerPage: 10, searchText: '', flagExportData: false };
		await handleClearScriptDateUpdates();
		handleGetScriptDates(selectedTypes, selectedYear, query, exportData, selectedTeamIds);
		setDisplayAppointmentsName('Filtered');
	}, [handleGetScriptDates]);

	useEffect(() => {
		setViewDataOpen(false);
		setIsYearChange(true);
    }, [year]);   
	
	useEffect(() => {
		if (refinerOpen && refineData.length == 0) {
			//_fetch all data to fill refinement fields.
			getRefineData();
		}
    }, [refinerOpen]);

	const getRefineData = () => {	
		const exportData = true, isRefine = true;
		resetDataTable(exportData, isRefine);
	};

	let refineSelectors = [];
	function onRefinementSelectorsChange(selectors, blankValue) {
		if(refineData.length > 0 && selectors && selectors.length > 0) 
		{
			//_setting default value of "Not Set".						
			const notSet = blankValue;				
			let title = '';
			let markingTeamCode = '';
			let markingMethod = [];				
			let scriptDeliveryDate = '';
			let scriptReturnDate = '';
			let eMarkingStartDate = '';
			let eMarkingEndDate = '';			
			let allValidSelection = true;	

			selectors.forEach(item => {
				const selectedItem = item.field;				
				const data = item.value || [];
				let isNotSetAdded = false;

				if(item.isValid) {
					switch (selectedItem) {					
					case 'title':
						title = data || '';
						break;
					case 'markingTeamCode':
						markingTeamCode = data || '';
						break;
					case 'markingMethodCurrentYear':
						data.forEach(x => {
							if(x == notSet && !isNotSetAdded) 
							{
								markingMethod.push('0');
								isNotSetAdded = true;
							}
							else 
							{
								markingMethod.push(x);
							}
						});
						break;
					case 'scriptDeliveryDate':
						scriptDeliveryDate = formatDateInput(data) || '';
						break;
					case 'scriptReturnDate':
						scriptReturnDate = formatDateInput(data) || '';
						break;
					case 'eMarkingStartDate':
						eMarkingStartDate = formatDateInput(data) || '';
						break;
					case 'eMarkingEndDate':
						eMarkingEndDate = formatDateInput(data) || '';
						break;
					default:
						return;
					}
				}
				else
				{
					allValidSelection = false;
				}
			});
			
			if(allValidSelection) {
				const exportData = false, isRefine = false;
				const refineFields = [{ title, markingTeamCode, markingMethod, scriptDeliveryDate, scriptReturnDate, eMarkingStartDate, eMarkingEndDate }];
				resetDataTable(exportData, isRefine, refineFields);
				refineSelectors = [...selectors];
			}			
			setIsRefineRemove(false);
		}
		else {
			refineSelectors = [];
		}
	};

	function onRefinementSelectorsRemove() {
		setIsRefineRemove(true);		
	};

	useEffect(() => {
		if(isRefineRemove && refineSelectors.length == 0) {
			resetDataTable();
		}
    }, [isRefineRemove]);

	function onRefinementAllClear() {
		refineSelectors = [];
		setIsRefineAllClear(true);
	};

	useEffect(() => {
		if(isRefineAllClear) {
			setIsRefineAllClear(false);
			resetDataTable();
		}
    }, [isRefineAllClear]);

	function setPageDetails(pageSize) {
		const size = pageSize || 10
		setPageSize(size);
	};

	function resetPageNo(isPageReset = false) {
		setIsPageReset(isPageReset);
	};

	const startNewUpdate = async () => {
		setIsSave(false);
		handleClearRefineFilters();
		handleClearGroupUpdaterData();
		handleClearRefineData();
		refineSelectors = [];		
        setIsRefineAllClear(true);	
		setRefinerOpen(false);
		setUpdaterOpen(false);
		setDisplayAppointmentsName('Filtered');
		resetPageNo(true);
    };

	const getGroupUpdaterData = async () => {
		const exportData = true, isRefine = false;
		const groupUpdaterFields = [{ isGroupUpdater: true, previewUpdate: [], isGroupUpdaterFilter: false }];		
		await resetDataTable(exportData, isRefine, refineFields, groupUpdaterFields);
	};
	
	const updateDataTable = async (groupUpdaterSelections = [], displayName = '') => {	
		if(!isSave) {
			const exportData = false, isRefine = false, isGroupUpdater = false, isGroupUpdaterFilter = false;
			let groupUpdaterFields = [{ isGroupUpdater, previewUpdate: [], isGroupUpdaterFilter }];		
			if (groupUpdaterSelections.length > 0) {
				let previewUpdate = [{ eMarkingStartDate: null, eMarkingEndDate: null }];

				groupUpdaterSelections.forEach(item => {
					const field = item.field;
					switch (field) {	
						case fields.E_MarkingStartDate:
							previewUpdate[0].eMarkingStartDate = item.value;
							break;
						case fields.E_MarkingEndDate:
							previewUpdate[0].eMarkingEndDate = item.value;
							break;
					}							
				});
				groupUpdaterFields = [{ isGroupUpdater, previewUpdate, isGroupUpdaterFilter }];
				setPreviewUpdate(previewUpdate);
				setDisplayAppointmentsName(displayName); 

				await resetDataTable(exportData, isRefine, refineFields, groupUpdaterFields);
				getGroupUpdaterData();
			}
			else {
				await resetDataTable(exportData, isRefine, refineFields, groupUpdaterFields);
			}
		}
	}; 

	const saveData = async () => {
		let updates = [...groupUpdaterData] || [];
		let updatedIds = [], failedIds = [];

		if (previewUpdate && previewUpdate.length > 0) {
			if (updates.length == 0) {
				await getGroupUpdaterData();
			 	updates = [...groupUpdaterData];
			}

			const { eMarkingStartDate = null, eMarkingEndDate = null } = previewUpdate[0];
			updates.forEach(element => {
				if(eMarkingStartDate) {
					element.eMarkingStartDate = eMarkingStartDate;
				}
				if(eMarkingEndDate) {
					element.eMarkingEndDate = eMarkingEndDate;
				}
			});
			
			await saveGroupUpdater(types, year, updates);
			handleGetGroupUpdaterUpdates();

			let isSaveData = false;
			const updatedData = [...groupUpdaterData] || [];
			updatedData.forEach(item => {
				if(item.isSave)	{
					updatedIds.push({ type: item.type, markingTeamId: item.markingTeamId });

					if(!isSaveData) {
						isSaveData = true;	
					}
				}
				else
				{
					failedIds.push({ type: item.type, markingTeamId: item.markingTeamId });
				}
			});
			setIsSave(isSaveData);

			const updatedAll = (updatedData.length == updatedIds.length);
			setUpdatedIds(updatedIds);
			setFailedIds(failedIds);	

			const result = { updatedAll, updatedIds, failedIds };
			return result;
		}
	}; 

	//_common method to display SAVED, FAILED and REFINED data.
	const displayData = (viewUpdatesType, displayName = '') => {
		const exportData = false, isRefine = false; //, isGroupUpdater = false, previewUpdate = [];
		let groupUpdaterTypes = [], groupUpdaterTeamIds = [];
		let groupUpdaterFields = [{ isGroupUpdater: false, previewUpdate: [], isGroupUpdaterFilter: true }];
		switch (viewUpdatesType) {
			case viewUpdates.PartialSaved:
				updatedIds.forEach(item => {
					groupUpdaterTypes.push(item.type); 
					groupUpdaterTeamIds.push(item.markingTeamId); 
				});			
				break;
			case viewUpdates.Failed:
				failedIds.forEach(item => {
					groupUpdaterTypes.push(item.type); 
					groupUpdaterTeamIds.push(item.markingTeamId); 
				});			
				break;			
			case viewUpdates.Refined:
				groupUpdaterFields = [{ isGroupUpdater: false, previewUpdate: [], isGroupUpdaterFilter: false }];
				break;
		}	
		setDisplayAppointmentsName(displayName);							
		handleGroupUpdaterResultsDetails(groupUpdaterTypes, groupUpdaterTeamIds);				
		resetDataTable(exportData, isRefine, refineFields, groupUpdaterFields);
	};

	const resetDataTable = async(exportData = false, isRefine = false, refineFields = [], groupUpdaterFields = []) => {
		if(viewDataOpen) {
			const query = { sortOrder: 'asc', sortField: 'title', page: 1, sizePerPage: pageSize, searchText: '', flagExportData: exportData };

			await handleGetScriptDates(selectedTypes, year, query, exportData, selectedTeamIds, isRefine, refineFields, groupUpdaterFields);
		}
	};

    return { 
        filterOpen, toggleFilter, isFiltering, setIsFiltering, year, updateYear, startNewFilter, handleFilterClear, handleFilterSubmit
		, refinerFields, refinerOpen, inPreview, refineData, onRefinementSelectorsChange, onRefinementSelectorsRemove, onRefinementAllClear, toggleRefiner		
		, updaterOpen, toggleUpdater, setInPreview, startNewUpdate, updateDataTable, saveData, groupUpdaterFields, displayData, groupUpdaterData
        , displayAppointmentsName, viewDataOpen, setViewDataOpen, toggleViewData, isYearChange, isLoaded, selectedTypes, selectedTeamIds
		, setPageDetails, refineFields, resetPageNo, isPageReset
    };
};

useScriptDates.propTypes = {
    handleClearScriptDateUpdates: PropTypes.func.isRequired,
	handleGetScriptDates: PropTypes.func.isRequired
};

export default useScriptDates;